import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Raleway', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="bg-[#1C2B1F] pt-36 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block text-amber-400 text-xs font-bold uppercase tracking-widest mb-3">Legal</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-stone-300 text-base">Last updated: 15 April 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose max-w-none text-zinc-700 leading-relaxed space-y-10">

            <div>
              <p className="text-zinc-600 text-sm bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-md">
                Ensure Windows (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is committed to protecting and respecting your privacy. This policy explains how we collect, use, and safeguard your personal information when you use our website or services.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">1</span>
                Who We Are
              </h2>
              <p>Ensure Windows is a windows, doors, and glazing company operating across Surrey and South London. Our registered address is:</p>
              <address className="not-italic mt-3 bg-stone-50 p-4 rounded-lg text-sm">
                Ensure Windows<br />
                71-75 Shelton Street<br />
                London WC2H 9JQ<br />
                Email: <a href="mailto:sales@ensurewindows.co.uk" className="text-amber-600 hover:underline">sales@ensurewindows.co.uk</a><br />
                Tel: <a href="tel:07852636300" className="text-amber-600 hover:underline">07852 636 300</a>
              </address>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">2</span>
                Information We Collect
              </h2>
              <p className="mb-3">We may collect the following types of personal information:</p>
              <ul className="space-y-2 ml-4">
                {[
                  "Name, address, email address, and telephone number",
                  "Property details relevant to your enquiry or order",
                  "Financial information required to process payments",
                  "IP address and browser data collected via cookies",
                  "Communication records including emails and enquiry forms",
                  "CCTV footage if you visit our showroom",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-amber-500 text-xs"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">3</span>
                How We Use Your Information
              </h2>
              <p className="mb-3">We use your personal information to:</p>
              <ul className="space-y-2 ml-4">
                {[
                  "Process and fulfil your orders and service requests",
                  "Provide quotes, surveys, and installation services",
                  "Send service updates, booking confirmations, and invoices",
                  "Respond to your enquiries and complaints",
                  "Send marketing communications where you have opted in",
                  "Comply with legal and regulatory obligations",
                  "Improve our website and services",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-amber-500"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">4</span>
                Legal Basis for Processing
              </h2>
              <p className="mb-3">We process your data under the following legal bases:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { basis: "Contract", desc: "To fulfil a contract with you or take steps at your request before entering into one" },
                  { basis: "Legal Obligation", desc: "To comply with a legal obligation we are subject to" },
                  { basis: "Legitimate Interests", desc: "For our legitimate business interests such as preventing fraud and improving services" },
                  { basis: "Consent", desc: "Where you have given clear consent for us to process your data for a specific purpose" },
                ].map((item) => (
                  <div key={item.basis} className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                    <strong className="text-zinc-900 text-sm block mb-1">{item.basis}</strong>
                    <p className="text-zinc-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">5</span>
                Data Sharing
              </h2>
              <p className="mb-3">We do not sell your personal data. We may share it with:</p>
              <ul className="space-y-2 ml-4 text-sm">
                <li className="flex items-start gap-2"><div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-arrow-right-s-line text-amber-500"></i></div>Sub-contractors and installation teams who assist in delivering your order</li>
                <li className="flex items-start gap-2"><div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-arrow-right-s-line text-amber-500"></i></div>Payment processors and financial institutions</li>
                <li className="flex items-start gap-2"><div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-arrow-right-s-line text-amber-500"></i></div>FENSA or other trade body registrations as required</li>
                <li className="flex items-start gap-2"><div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-arrow-right-s-line text-amber-500"></i></div>Legal and regulatory authorities when required by law</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">6</span>
                Data Retention
              </h2>
              <p>We retain personal data only for as long as necessary to fulfil the purposes it was collected for, including legal, accounting, or reporting requirements. Customer records are typically retained for 7 years following the completion of a contract, in line with HMRC requirements. After this period, data is securely deleted or anonymised.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">7</span>
                Cookies
              </h2>
              <p>Our website uses cookies to improve your experience. You can manage your cookie preferences at any time. For full details, please see our <Link to="/cookies" className="text-amber-600 hover:underline font-medium">Cookie Policy</Link>.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">8</span>
                Your Rights
              </h2>
              <p className="mb-3">Under UK GDPR, you have the following rights:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: "ri-eye-line", right: "Right to Access", desc: "Request a copy of the data we hold about you" },
                  { icon: "ri-edit-line", right: "Right to Rectification", desc: "Ask us to correct inaccurate or incomplete data" },
                  { icon: "ri-delete-bin-line", right: "Right to Erasure", desc: "Request deletion of your personal data in certain circumstances" },
                  { icon: "ri-pause-circle-line", right: "Right to Restriction", desc: "Ask us to restrict processing of your data" },
                  { icon: "ri-download-line", right: "Right to Portability", desc: "Receive your data in a structured, machine-readable format" },
                  { icon: "ri-close-circle-line", right: "Right to Object", desc: "Object to processing based on legitimate interests or for direct marketing" },
                ].map((item) => (
                  <div key={item.right} className="flex items-start gap-3 bg-stone-50 p-3 rounded-lg border border-stone-200">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                      <i className={`${item.icon} text-amber-600 text-sm`}></i>
                    </div>
                    <div>
                      <strong className="text-zinc-900 text-sm block">{item.right}</strong>
                      <p className="text-zinc-600 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm">To exercise any of these rights, contact us at <a href="mailto:sales@ensurewindows.co.uk" className="text-amber-600 hover:underline">sales@ensurewindows.co.uk</a>. You also have the right to lodge a complaint with the <a href="https://ico.org.uk" rel="nofollow" target="_blank" className="text-amber-600 hover:underline">Information Commissioner&apos;s Office (ICO)</a>.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">9</span>
                Security
              </h2>
              <p>We take appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, or destruction. Where we use third-party processors, we ensure they provide sufficient guarantees regarding data security.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 text-sm font-bold">10</span>
                Changes to This Policy
              </h2>
              <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. We encourage you to review this page periodically.</p>
            </div>

            <div className="bg-[#1C2B1F] text-white rounded-xl p-6 mt-8">
              <h3 className="font-bold text-lg mb-2">Contact Us About Your Privacy</h3>
              <p className="text-stone-300 text-sm mb-4">If you have any questions about this Privacy Policy or how we handle your data, please get in touch.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="mailto:sales@ensurewindows.co.uk" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line"></i></div>
                  sales@ensurewindows.co.uk
                </a>
                <a href="tel:07852636300" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-line"></i></div>
                  07852 636 300
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
